import { loadLegendMeasurement } from '../../composition/legendMeasurement';
import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

import {
  clampNormalizedPoint,
  compositionPageDimensionsMm,
  fitPagePreview,
  moveNormalizedRect,
  resizeNormalizedRect,
  resizeNormalizedRectWithAspect,
  type ResizeHandle,
} from '../../composition/geometry';
import {
  snapCompositionRect,
  type CompositionSnapTarget,
  type CompositionSnapResult,
} from '../../composition/snapping';
import { deriveCompositionLegend } from '../../composition/legend';
import {
  effectiveLegendCornerRadius,
  fittedLegendBounds,
  legendBoundsEqual,
  buildLegendLayout,
  type LegendTextMeasurement,
  type LegendPlannedText,
  wrapLegendText,
} from '../../composition/legendLayout';
import { type NormalizedPoint, type NormalizedRect } from '../../composition/model';
import { deriveScaleBarMeasurement, northArrowRotationDegrees } from '../../composition/furniture';
import { COMPOSITION_LOGO_MIN_NORMALIZED_SIZE } from '../../composition/logo';
import { effectiveCompositionCamera, hasExactCompositionScale } from '../../composition/scale';
import { compositionTypographyStyle } from '../../composition/typography';
import { Icon } from '../../components/Icon';
import type { ApplicationMode } from '../modes/applicationModes';
import type { CompositionElementSelection } from '../state/appStore';
import { useAppStore } from '../state/useAppStore';
import { AnnotationLegendSymbolPreview, SymbolPreview } from './style/SymbolPreview';
import { staticBasemapAttributionText } from '../../basemaps/catalog';
import { InsetMap } from './composition/InsetMap';
import type { GroundFootprint } from '../../composition/inset';
import { NorthArrowSymbol } from './composition/NorthArrowSymbol';
import { scaleBarLayout } from '../../composition/panelAppearance';
import { scaleBarStrokeWidthMm } from '../../composition/scaleBarStroke';

export interface CompositionRenderContext {
  readonly pixelsPerMm: number;
  readonly onGroundFootprint: (footprint: GroundFootprint | null, zoom: number) => void;
}

interface CompositionCanvasProps {
  readonly renderMap: (context: CompositionRenderContext) => ReactNode;
  readonly mode: Exclude<ApplicationMode, 'empty'>;
}

type RectElement = 'sources-attribution' | 'inset-map' | 'legend' | 'logo' | 'map-frame';
type PointElement = 'north-arrow' | 'scale-bar' | 'sources-attribution' | 'subtitle' | 'title';
type DragState =
  | {
      readonly kind: 'move-rect';
      readonly element: RectElement;
      readonly pointerId: number;
      readonly startClient: NormalizedPoint;
      readonly startRect: NormalizedRect;
    }
  | {
      readonly kind: 'resize-rect';
      readonly element: RectElement;
      readonly pointerId: number;
      readonly startClient: NormalizedPoint;
      readonly startRect: NormalizedRect;
      readonly corner: ResizeHandle;
    }
  | {
      readonly kind: 'move-point';
      readonly pointerId: number;
      readonly startClient: NormalizedPoint;
      readonly startPosition: NormalizedPoint;
      readonly element: PointElement;
    };

type DragStart = DragState extends infer Candidate
  ? Candidate extends DragState
    ? Omit<Candidate, 'pointerId' | 'startClient'>
    : never
  : never;

const SNAP_TOLERANCE_PX = 7;

const pointStyle = (point: NormalizedPoint): CSSProperties => ({
  left: `${point.x * 100}%`,
  top: `${point.y * 100}%`,
});
const rectStyle = (rect: NormalizedRect): CSSProperties => ({
  left: `${rect.x * 100}%`,
  top: `${rect.y * 100}%`,
  width: `${rect.width * 100}%`,
  height: `${rect.height * 100}%`,
});

function arrowDelta(event: React.KeyboardEvent): NormalizedPoint | null {
  const amount = event.shiftKey ? 0.02 : 0.005;
  if (event.key === 'ArrowLeft') return { x: -amount, y: 0 };
  if (event.key === 'ArrowRight') return { x: amount, y: 0 };
  if (event.key === 'ArrowUp') return { x: 0, y: -amount };
  if (event.key === 'ArrowDown') return { x: 0, y: amount };
  return null;
}

function colourWithOpacity(colour: string, opacity: number): string {
  const value = colour.slice(1);
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
}

function ResizeHandles({
  label,
  onResize,
  horizontalOnly = false,
}: {
  readonly label: string;
  readonly horizontalOnly?: boolean;
  readonly onResize: (event: ReactPointerEvent<HTMLButtonElement>, corner: ResizeHandle) => void;
}) {
  return (
    [
      'north-west',
      'north-east',
      'south-west',
      'south-east',
      'north',
      'east',
      'south',
      'west',
    ] as const
  )
    .filter((handle) => !horizontalOnly || (handle !== 'north' && handle !== 'south'))
    .map((corner) => (
      <button
        aria-label={`Resize ${label} ${corner}`}
        className={`composition-resize-handle composition-resize-${corner}`}
        key={corner}
        onPointerDown={(event) => onResize(event, corner)}
        tabIndex={-1}
        type="button"
      />
    ));
}

export function CompositionCanvas({ renderMap, mode }: CompositionCanvasProps) {
  const [snapGuides, setSnapGuides] = useState<CompositionSnapResult['guides']>({});
  const dragObjectsRef = useRef<readonly CompositionSnapTarget[]>([]);
  const draggedBoundsRef = useRef<NormalizedRect | null>(null);
  const objectSnapEnabled = useAppStore((state) => state.shell.compositionObjectSnapEnabled);
  const [fontRevision, setFontRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setFontRevision((value) => value + 1);
    document.fonts?.addEventListener('loadingdone', refresh);
    return () => document.fonts?.removeEventListener('loadingdone', refresh);
  }, []);
  const [legendMeasurement, setLegendMeasurement] = useState<{
    key: string;
    measure: LegendTextMeasurement;
  } | null>(null);
  const [legendFontError, setLegendFontError] = useState(false);
  const [ground, setGround] = useState<{ footprint: GroundFootprint | null; zoom: number }>({
    footprint: null,
    zoom: 0,
  });
  const observeGround = useCallback(
    (footprint: GroundFootprint | null, zoom: number) => setGround({ footprint, zoom }),
    [],
  );
  const updateInset = useAppStore((state) => state.updateCompositionInsetMap);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const pasteboardPress = useRef<{
    readonly pointerId: number;
    readonly x: number;
    readonly y: number;
  } | null>(null);
  const composition = useAppStore((state) => state.project.composition);
  const basemap = useAppStore((state) => state.project.map.basemap);
  const sources = useAppStore((state) => state.project.sources);
  const layers = useAppStore((state) => state.project.layers);
  const annotationLayers = useAppStore((state) => state.project.annotationLayers);
  const selected = useAppStore((state) => state.shell.selectedCompositionElement);
  const drawingAnnotation = useAppStore((state) => state.shell.annotationCreationTool);
  const selectedAnnotation = useAppStore((state) => state.shell.selectedAnnotationId);
  const gridVisible = useAppStore((state) => state.shell.compositionGridVisible);
  const gridSpacingMm = useAppStore((state) => state.shell.compositionGridSpacingMm);
  const snapEnabled = useAppStore((state) => state.shell.compositionSnapEnabled);
  const selectElement = useAppStore((state) => state.selectCompositionElement);
  const updateBounds = useAppStore((state) => state.updateCompositionMapFrameBounds);
  const updateText = useAppStore((state) => state.updateCompositionText);
  const updateLegend = useAppStore((state) => state.updateCompositionLegend);
  const updateLogo = useAppStore((state) => state.updateCompositionLogo);
  const updateScaleBar = useAppStore((state) => state.updateCompositionScaleBar);
  const updateNorthArrow = useAppStore((state) => state.updateCompositionNorthArrow);
  const updateSourcesAttribution = useAppStore(
    (state) => state.updateCompositionSourcesAttribution,
  );
  const setPreviewPixelsPerMm = useAppStore((state) => state.setCompositionPreviewPixelsPerMm);
  const [available, setAvailable] = useState({ width: 900, height: 650 });
  const active = mode === 'composition';

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !active) return;
    const measure = () => {
      const bounds = stage.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0)
        setAvailable({ width: bounds.width, height: bounds.height });
    };
    measure();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [active]);

  const { preset, orientation, customWidthMm, customHeightMm } = composition.page;
  const pageMm = useMemo(
    () => compositionPageDimensionsMm({ preset, orientation, customWidthMm, customHeightMm }),
    [preset, orientation, customWidthMm, customHeightMm],
  );
  const previousLegendPageMm = useRef(pageMm);
  const preview = fitPagePreview(available, pageMm);
  // Live inset observations must not reapply the older authored main camera mid-gesture.
  const mapNode = useMemo(
    () => renderMap({ pixelsPerMm: preview.pixelsPerMm, onGroundFootprint: observeGround }),
    [renderMap, preview.pixelsPerMm, observeGround],
  );
  useEffect(() => {
    if (active) setPreviewPixelsPerMm(preview.pixelsPerMm);
  }, [active, preview.pixelsPerMm, setPreviewPixelsPerMm]);
  const legendLayers = useMemo(
    () =>
      deriveCompositionLegend(
        sources,
        layers,
        composition.legend.excludedLayerIds,
        composition.legend.excludedEntryKeysByLayer,
        annotationLayers,
        composition.legend.hiddenGroupHeadingIds,
      ),
    [
      sources,
      layers,
      composition.legend.excludedLayerIds,
      composition.legend.excludedEntryKeysByLayer,
      composition.legend.hiddenGroupHeadingIds,
      annotationLayers,
    ],
  );
  const fontKey = [
    composition.legend.bodyTypography.fontFamily,
    composition.legend.bodyTypography.fontWeight,
    composition.legend.bodyTypography.fontStyle,
    composition.legend.heading.enabled,
    composition.legend.heading.typography.fontFamily,
    composition.legend.heading.typography.fontWeight,
    composition.legend.heading.typography.fontStyle,
  ].join(':');
  const fontSettingsRef = useRef(composition.legend);
  useEffect(() => {
    fontSettingsRef.current = composition.legend;
  }, [composition.legend]);
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void loadLegendMeasurement(fontSettingsRef.current)
      .then((measure) => {
        if (!cancelled) {
          setLegendMeasurement({ key: fontKey, measure });
          setLegendFontError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLegendFontError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [active, fontKey]);
  const legendMeasure = legendMeasurement?.key === fontKey ? legendMeasurement.measure : undefined;
  const legendLayout = useMemo(
    () =>
      buildLegendLayout(
        composition.legend,
        legendLayers,
        composition.legend.bounds.width * pageMm.width,
        composition.legend.bounds.height * pageMm.height,
        legendMeasure,
      ),
    [composition.legend, legendLayers, pageMm, legendMeasure],
  );
  const legendOverflow = legendLayout.overflow || legendFontError;
  useEffect(() => {
    const previous = previousLegendPageMm.current;
    previousLegendPageMm.current = pageMm;
    if (previous.width !== pageMm.width || previous.height !== pageMm.height) return;
    if (!active || !composition.legend.autoFit) return;
    const bounds = fittedLegendBounds(
      composition.legend,
      legendLayers,
      composition.mapFrame.bounds,
      pageMm,
      {
        right: composition.legend.bounds.x + composition.legend.bounds.width,
        bottom: composition.legend.bounds.y + composition.legend.bounds.height,
        ...(composition.legend.autoFitAnchor === 'top-right'
          ? { top: composition.legend.bounds.y }
          : {}),
      },
      legendMeasure,
    );
    if (!legendBoundsEqual(bounds, composition.legend.bounds)) updateLegend({ bounds });
  }, [
    active,
    composition.legend,
    composition.mapFrame.bounds,
    legendLayers,
    pageMm,
    updateLegend,
    fontRevision,
    legendMeasure,
  ]);
  const legendBorderMm = composition.legend.border.enabled ? composition.legend.border.widthMm : 0;
  const legendLines = (text: LegendPlannedText) => {
    const normalized = text.value.trim().replace(/\s+/g, ' ');
    let offset = 0;
    return text.lines.map((line, index) => {
      offset += line.length;
      const separator = normalized[offset] === ' ' ? ' ' : '';
      offset += separator.length;
      // Explicit line boxes retain the original text for copying/accessibility, even mid-token.
      return (
        <span key={index} style={{ display: 'block' }}>
          {line}
          <span style={{ fontSize: 0 }}>{separator}</span>
        </span>
      );
    });
  };
  const legendTextStyle = (text: LegendPlannedText): CSSProperties => ({
    ...compositionTypographyStyle(text.typography, preview.pixelsPerMm),
    position: 'absolute',
    left: (text.x - legendBorderMm) * preview.pixelsPerMm,
    top: (text.y - legendBorderMm) * preview.pixelsPerMm,
    width: text.width * preview.pixelsPerMm,
    lineHeight: `${text.lineHeight * preview.pixelsPerMm}px`,
    whiteSpace: 'pre',
    margin: 0,
  });
  const tolerance = { x: SNAP_TOLERANCE_PX / preview.width, y: SNAP_TOLERANCE_PX / preview.height };
  const scaleBarMeasurement = useMemo(
    () =>
      deriveScaleBarMeasurement(
        effectiveCompositionCamera(
          composition.mapFrame.camera,
          composition.mapFrame.bounds.width * pageMm.width,
          composition.mapFrame.bounds.height * pageMm.height,
          preview.pixelsPerMm,
        ).scaleDenominator,
        composition.scaleBar.maximumWidthMm,
        composition.scaleBar.units,
      ),
    [
      composition.mapFrame,
      pageMm.width,
      pageMm.height,
      preview.pixelsPerMm,
      composition.scaleBar.maximumWidthMm,
      composition.scaleBar.units,
    ],
  );
  const mandatoryAttribution = staticBasemapAttributionText(basemap);

  const updateRect = (element: RectElement, bounds: NormalizedRect) => {
    if (element === 'sources-attribution') {
      const elementBox = stageRef.current?.querySelector('.composition-sources-attribution');
      const textStyle = elementBox ? getComputedStyle(elementBox) : null;
      const context = document.createElement('canvas').getContext('2d');
      if (context && textStyle) context.font = textStyle.font;
      const lineCount = context
        ? wrapLegendText(
            mandatoryAttribution ?? '',
            bounds.width * preview.width,
            (value) => context.measureText(value).width,
          ).length
        : 1;
      const height =
        (lineCount * (textStyle ? parseFloat(textStyle.fontSize) * 1.15 : 0)) / preview.height;
      updateSourcesAttribution({
        width: bounds.width,
        position: {
          x: bounds.x,
          y: Math.max(0, Math.min(composition.sourcesAttribution.position.y, 1 - height)),
        },
      });
    } else if (element === 'map-frame') updateBounds(bounds);
    else if (element === 'legend') updateLegend({ bounds });
    else if (element === 'inset-map' && composition.insetMap)
      updateInset({ ...composition.insetMap, bounds });
    else if (composition.logo) updateLogo({ ...composition.logo, bounds });
  };

  const updatePoint = (element: PointElement, position: NormalizedPoint) => {
    if (element === 'title' || element === 'subtitle') updateText(element, { position });
    else if (element === 'scale-bar') updateScaleBar({ position });
    else if (element === 'north-arrow') updateNorthArrow({ position });
    else {
      const box = stageRef.current
        ?.querySelector('.composition-sources-attribution')
        ?.getBoundingClientRect();
      updateSourcesAttribution({
        position: {
          x: Math.max(0, Math.min(1 - (box?.width ?? 0) / preview.width, position.x)),
          y: Math.max(0, Math.min(1 - (box?.height ?? 0) / preview.height, position.y)),
        },
        width: Math.min(1, (box?.width ?? preview.width * 0.5) / preview.width),
      });
    }
  };

  const authoredRectFor = (id: string): NormalizedRect | undefined =>
    id === 'map-frame'
      ? composition.mapFrame.bounds
      : id === 'legend'
        ? composition.legend.bounds
        : id === 'inset-map'
          ? composition.insetMap?.bounds
          : id === 'logo'
            ? composition.logo?.bounds
            : undefined;

  const beginDrag = (
    event: ReactPointerEvent<HTMLElement>,
    drag: DragStart,
    element: Exclude<CompositionElementSelection, null>,
  ) => {
    if (!active || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const pageBox = stageRef.current?.querySelector('.composition-page')?.getBoundingClientRect();
    if (pageBox && pageBox.width > 0 && pageBox.height > 0) {
      const ids = [
        'map-frame',
        'legend',
        'inset-map',
        'sources-attribution',
        'title',
        'subtitle',
        'scale-bar',
        'north-arrow',
        'logo',
      ] as const;
      dragObjectsRef.current = ids.flatMap((id) => {
        const node = stageRef.current?.querySelector<HTMLElement>(`.composition-${id}`);
        if (
          !node ||
          !node.getClientRects().length ||
          getComputedStyle(node).visibility === 'hidden'
        )
          return [];
        const box = node.getBoundingClientRect();
        // Rectangles snap to exact authored edges; point furniture supplies rendered dimensions.
        const authored = authoredRectFor(id);
        const position =
          id === 'title'
            ? composition.title.position
            : id === 'subtitle'
              ? composition.subtitle.position
              : id === 'scale-bar'
                ? composition.scaleBar.position
                : id === 'north-arrow'
                  ? composition.northArrow.position
                  : id === 'sources-attribution'
                    ? composition.sourcesAttribution.position
                    : undefined;
        return [
          {
            id,
            bounds: authored ?? {
              x: position?.x ?? (box.left - pageBox.left) / pageBox.width,
              y: position?.y ?? (box.top - pageBox.top) / pageBox.height,
              width: box.width / pageBox.width,
              height: box.height / pageBox.height,
            },
          },
        ];
      });
      draggedBoundsRef.current =
        dragObjectsRef.current.find(({ id }) => id === element)?.bounds ?? null;
    }
    setSnapGuides({});
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      ...drag,
      pointerId: event.pointerId,
      startClient: { x: event.clientX, y: event.clientY },
    } as DragState;
    selectElement(element);
  };

  const snapRect = (
    bounds: NormalizedRect,
    element: string,
    handle?: ResizeHandle,
    minimumSize = 0.05,
    pointGrid = false,
    aspectRatio?: number,
    allowPointOverflow = false,
  ) => {
    const result = snapCompositionRect(bounds, {
      objectId: element,
      objects: dragObjectsRef.current.map((object) => ({
        ...object,
        bounds: authoredRectFor(object.id) ?? object.bounds,
      })),
      grid: snapEnabled,
      objectSnap: objectSnapEnabled,
      page: pageMm,
      gridSpacingMm,
      tolerance,
      handle,
      minimumSize,
      pointGrid,
      aspectRatio,
      allowPointOverflow,
    });
    setSnapGuides(result.guides);
    return result.bounds;
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = {
      x: (event.clientX - drag.startClient.x) / preview.width,
      y: (event.clientY - drag.startClient.y) / preview.height,
    };
    if (drag.kind === 'move-rect') {
      const moved = moveNormalizedRect(drag.startRect, delta);
      updateRect(drag.element, snapRect(moved, drag.element));
      return;
    }
    if (drag.kind === 'resize-rect') {
      if (drag.element === 'sources-attribution') {
        const west = drag.corner.endsWith('west');
        const right = drag.startRect.x + drag.startRect.width;
        const x = west
          ? Math.max(0, Math.min(right - 0.02, drag.startRect.x + delta.x))
          : drag.startRect.x;
        const width = west
          ? right - x
          : Math.max(0.02, Math.min(1 - x, drag.startRect.width + delta.x));
        updateRect(
          drag.element,
          snapRect({ ...drag.startRect, x, width }, drag.element, west ? 'west' : 'east', 0.02),
        );
        return;
      }
      const minimumSize =
        drag.element === 'logo'
          ? COMPOSITION_LOGO_MIN_NORMALIZED_SIZE
          : drag.element === 'legend' || drag.element === 'inset-map'
            ? 0.05
            : 0.1;
      const resized =
        drag.element === 'logo' && drag.corner.includes('-')
          ? resizeNormalizedRectWithAspect(
              drag.startRect,
              drag.corner,
              delta,
              minimumSize,
              drag.startRect.width / drag.startRect.height,
            )
          : resizeNormalizedRect(drag.startRect, drag.corner, delta, minimumSize);
      const bounds = snapRect(
        resized,
        drag.element,
        drag.corner,
        minimumSize,
        false,
        drag.element === 'logo' && drag.corner.includes('-')
          ? drag.startRect.width / drag.startRect.height
          : undefined,
      );
      if (drag.element === 'legend') updateLegend({ bounds, autoFit: false });
      else updateRect(drag.element, bounds);
      return;
    }
    let position = {
      x: drag.startPosition.x + delta.x,
      y: drag.startPosition.y + delta.y,
    };
    if (drag.element !== 'sources-attribution') position = clampNormalizedPoint(position);
    const rendered = draggedBoundsRef.current;
    if (rendered) {
      const pointDelta = {
        x: position.x - drag.startPosition.x,
        y: position.y - drag.startPosition.y,
      };
      const moved =
        drag.element === 'sources-attribution'
          ? moveNormalizedRect(rendered, pointDelta)
          : { ...rendered, x: rendered.x + pointDelta.x, y: rendered.y + pointDelta.y };
      const snapped = snapRect(
        moved,
        drag.element,
        undefined,
        0.05,
        true,
        undefined,
        drag.element !== 'sources-attribution',
      );
      position = {
        x: drag.startPosition.x + snapped.x - rendered.x,
        y: drag.startPosition.y + snapped.y - rendered.y,
      };
      if (drag.element !== 'sources-attribution') position = clampNormalizedPoint(position);
    }
    updatePoint(drag.element, position);
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      dragObjectsRef.current = [];
      draggedBoundsRef.current = null;
      setSnapGuides({});
    }
  };

  const nudgePoint = (
    event: React.KeyboardEvent,
    element: PointElement,
    position: NormalizedPoint,
  ) => {
    const delta = arrowDelta(event);
    if (!delta) return;
    event.preventDefault();
    updatePoint(
      element,
      element === 'sources-attribution'
        ? { x: position.x + delta.x, y: position.y + delta.y }
        : clampNormalizedPoint({ x: position.x + delta.x, y: position.y + delta.y }),
    );
  };

  const nudgeRect = (event: React.KeyboardEvent, element: RectElement, bounds: NormalizedRect) => {
    const delta = arrowDelta(event);
    if (!delta) return;
    event.preventDefault();
    updateRect(element, moveNormalizedRect(bounds, delta));
  };

  const pageStyle: CSSProperties = active
    ? {
        width: `${preview.width}px`,
        height: `${preview.height}px`,
        backgroundColor: composition.page.backgroundColour,
      }
    : {};
  const frameStyle: CSSProperties = active
    ? ({
        ...rectStyle(composition.mapFrame.bounds),
        '--composition-map-border-colour': composition.mapFrame.border.enabled
          ? composition.mapFrame.border.colour
          : 'transparent',
        '--composition-map-border-width': `${composition.mapFrame.border.widthMm * preview.pixelsPerMm}px`,
        '--composition-map-radius': `${Math.min((composition.mapFrame.cornerRadiusMm ?? 0) * preview.pixelsPerMm, (composition.mapFrame.bounds.width * preview.width) / 2, (composition.mapFrame.bounds.height * preview.height) / 2)}px`,
      } as CSSProperties)
    : {};
  const gridStyle: CSSProperties = {
    backgroundSize: `${(gridSpacingMm / pageMm.width) * preview.width}px ${(gridSpacingMm / pageMm.height) * preview.height}px`,
  };

  return (
    <div
      className={`composition-canvas-host${active ? ' composition-canvas-active' : ''}`}
      ref={stageRef}
      tabIndex={active ? -1 : undefined}
      onPointerDownCapture={(event) => {
        pasteboardPress.current =
          active &&
          !drawingAnnotation &&
          !dragRef.current &&
          event.button === 0 &&
          event.isPrimary &&
          !event.ctrlKey &&
          event.target === event.currentTarget
            ? { pointerId: event.pointerId, x: event.clientX, y: event.clientY }
            : null;
      }}
      onPointerMoveCapture={(event) => {
        const press = pasteboardPress.current;
        if (
          press &&
          (press.pointerId !== event.pointerId ||
            Math.hypot(event.clientX - press.x, event.clientY - press.y) > 5)
        )
          pasteboardPress.current = null;
      }}
      onPointerUp={(event) => {
        const press = pasteboardPress.current;
        pasteboardPress.current = null;
        if (
          active &&
          !drawingAnnotation &&
          !dragRef.current &&
          press &&
          press.pointerId === event.pointerId &&
          event.button === 0 &&
          event.target === event.currentTarget &&
          Math.hypot(event.clientX - press.x, event.clientY - press.y) <= 5
        ) {
          selectElement(null);
          event.currentTarget.focus({ preventScroll: true });
        }
      }}
      onPointerLeave={() => {
        pasteboardPress.current = null;
      }}
      onPointerCancel={() => {
        pasteboardPress.current = null;
      }}
      onKeyDown={(event) => {
        if (
          !active ||
          !selected ||
          drawingAnnotation ||
          selectedAnnotation ||
          dragRef.current ||
          event.key !== 'Escape' ||
          event.defaultPrevented ||
          (event.target instanceof Element &&
            event.target.closest(
              'input, textarea, select, [contenteditable="true"], [role="menu"], [role="dialog"]',
            ))
        )
          return;
        event.preventDefault();
        event.stopPropagation();
        selectElement(null);
      }}
    >
      <div
        className={`composition-page${active ? ' composition-frame' : ''}`}
        data-page-orientation={composition.page.orientation}
        data-page-preset={composition.page.preset}
        onClick={(event) => {
          if (event.target === event.currentTarget) selectElement(null);
        }}
        onPointerDown={(event) => {
          if (event.target === event.currentTarget) selectElement(null);
        }}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        style={pageStyle}
      >
        {active && snapGuides.x !== undefined ? (
          <div
            aria-hidden="true"
            className="composition-snap-guide composition-snap-guide-x"
            style={{ left: `${snapGuides.x * 100}%` }}
          />
        ) : null}
        {active && snapGuides.y !== undefined ? (
          <div
            aria-hidden="true"
            className="composition-snap-guide composition-snap-guide-y"
            style={{ top: `${snapGuides.y * 100}%` }}
          />
        ) : null}
        {active && gridVisible ? (
          <div
            aria-hidden="true"
            className="composition-grid"
            data-grid-spacing-mm={gridSpacingMm}
            style={gridStyle}
          />
        ) : null}
        <div
          aria-label="Map frame"
          className={`composition-map-frame${active && selected === 'map-frame' ? ' composition-element-selected' : ''}`}
          onFocus={() => selectElement('map-frame')}
          onClick={() => selectElement('map-frame')}
          role="group"
          style={frameStyle}
          tabIndex={active ? 0 : undefined}
        >
          <div className="composition-map-content">
            <div className="composition-map-render-surface">{mapNode}</div>
          </div>
          {active && selected === 'map-frame' ? (
            <>
              <button
                aria-label="Move map frame"
                className="composition-move-handle"
                onKeyDown={(event) => nudgeRect(event, 'map-frame', composition.mapFrame.bounds)}
                onPointerDown={(event) =>
                  beginDrag(
                    event,
                    {
                      kind: 'move-rect',
                      element: 'map-frame',
                      startRect: composition.mapFrame.bounds,
                    },
                    'map-frame',
                  )
                }
                type="button"
              >
                <Icon name="move" />
              </button>
              <ResizeHandles
                label="map frame"
                onResize={(event, corner) =>
                  beginDrag(
                    event,
                    {
                      kind: 'resize-rect',
                      element: 'map-frame',
                      startRect: composition.mapFrame.bounds,
                      corner,
                    },
                    'map-frame',
                  )
                }
              />
            </>
          ) : null}
        </div>

        {active && composition.insetMap && composition.insetMap.enabled !== false ? (
          <div
            aria-label="Inset map"
            role="group"
            tabIndex={0}
            className={`composition-inset-map${selected === 'inset-map' ? ' composition-element-selected' : ''}`}
            style={{ ...rectStyle(composition.insetMap.bounds), position: 'absolute', zIndex: 3 }}
            onFocus={() => selectElement('inset-map')}
            onClick={() => selectElement('inset-map')}
            onKeyDown={(event) => nudgeRect(event, 'inset-map', composition.insetMap!.bounds)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                {
                  kind: 'move-rect',
                  element: 'inset-map',
                  startRect: composition.insetMap!.bounds,
                },
                'inset-map',
              )
            }
          >
            <InsetMap
              footprint={ground.footprint}
              mainZoom={ground.zoom}
              width={Math.round(composition.insetMap.bounds.width * preview.width)}
              height={Math.round(composition.insetMap.bounds.height * preview.height)}
              pixelsPerMm={preview.pixelsPerMm}
            />
            {composition.insetMap.border.enabled ? (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  boxSizing: 'border-box',
                  border: `${composition.insetMap.border.widthMm * preview.pixelsPerMm}px solid ${composition.insetMap.border.colour}`,
                  borderRadius: composition.insetMap.cornerRadiusMm * preview.pixelsPerMm,
                }}
              />
            ) : null}
            {selected === 'inset-map' ? (
              <ResizeHandles
                label="inset map"
                onResize={(event, corner) =>
                  beginDrag(
                    event,
                    {
                      kind: 'resize-rect',
                      element: 'inset-map',
                      startRect: composition.insetMap!.bounds,
                      corner,
                    },
                    'inset-map',
                  )
                }
              />
            ) : null}
          </div>
        ) : null}

        {active && composition.title.enabled ? (
          <div
            aria-label="Title"
            className={`composition-text composition-title${selected === 'title' ? ' composition-element-selected' : ''}`}
            onClick={() => selectElement('title')}
            onFocus={() => selectElement('title')}
            onKeyDown={(event) => nudgePoint(event, 'title', composition.title.position)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                { kind: 'move-point', element: 'title', startPosition: composition.title.position },
                'title',
              )
            }
            role="button"
            style={{
              ...pointStyle(composition.title.position),
              width: `${Math.max(0.02, 0.94 - composition.title.position.x) * 100}%`,
              ...compositionTypographyStyle(composition.title.typography, preview.pixelsPerMm),
            }}
            tabIndex={0}
          >
            {composition.title.text || 'Title'}
          </div>
        ) : null}

        {active && composition.subtitle.enabled ? (
          <div
            aria-label="Subtitle"
            className={`composition-text composition-subtitle${selected === 'subtitle' ? ' composition-element-selected' : ''}`}
            onClick={() => selectElement('subtitle')}
            onFocus={() => selectElement('subtitle')}
            onKeyDown={(event) => nudgePoint(event, 'subtitle', composition.subtitle.position)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                {
                  kind: 'move-point',
                  element: 'subtitle',
                  startPosition: composition.subtitle.position,
                },
                'subtitle',
              )
            }
            role="button"
            style={{
              ...pointStyle(composition.subtitle.position),
              width: `${Math.max(0.02, 0.94 - composition.subtitle.position.x) * 100}%`,
              ...compositionTypographyStyle(composition.subtitle.typography, preview.pixelsPerMm),
            }}
            tabIndex={0}
          >
            {composition.subtitle.text || 'Subtitle'}
          </div>
        ) : null}

        {active && mandatoryAttribution ? (
          <div
            aria-label="Sources & attribution"
            className={`composition-sources-attribution${selected === 'sources-attribution' ? ' composition-element-selected' : ''}`}
            onClick={() => selectElement('sources-attribution')}
            onFocus={() => selectElement('sources-attribution')}
            onKeyDown={(event) =>
              nudgePoint(event, 'sources-attribution', composition.sourcesAttribution.position)
            }
            onPointerDown={(event) =>
              beginDrag(
                event,
                {
                  kind: 'move-point',
                  element: 'sources-attribution',
                  startPosition: composition.sourcesAttribution.position,
                },
                'sources-attribution',
              )
            }
            role="button"
            style={{
              ...pointStyle(composition.sourcesAttribution.position),
              width: `${Math.min(composition.sourcesAttribution.width ?? Math.max(0, 1 - composition.sourcesAttribution.position.x), Math.max(0, 1 - composition.sourcesAttribution.position.x)) * 100}%`,
              ...compositionTypographyStyle(
                composition.sourcesAttribution.typography,
                preview.pixelsPerMm,
              ),
            }}
            tabIndex={0}
          >
            {mandatoryAttribution}
            {selected === 'sources-attribution' ? (
              <ResizeHandles
                label="sources attribution"
                horizontalOnly
                onResize={(event, corner) =>
                  beginDrag(
                    event,
                    {
                      kind: 'resize-rect',
                      element: 'sources-attribution',
                      corner,
                      startRect: {
                        ...composition.sourcesAttribution.position,
                        width:
                          composition.sourcesAttribution.width ??
                          1 - composition.sourcesAttribution.position.x,
                        height: 0.02,
                      },
                    },
                    'sources-attribution',
                  )
                }
              />
            ) : null}
          </div>
        ) : null}

        {active && composition.legend.enabled ? (
          <div
            aria-label="Legend"
            className={`composition-legend${selected === 'legend' ? ' composition-element-selected' : ''}`}
            onClick={() => selectElement('legend')}
            onFocus={() => selectElement('legend')}
            onKeyDown={(event) => nudgeRect(event, 'legend', composition.legend.bounds)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                { kind: 'move-rect', element: 'legend', startRect: composition.legend.bounds },
                'legend',
              )
            }
            role="group"
            data-auto-fit={composition.legend.autoFit}
            data-column-count={composition.legend.columnCount ?? 1}
            data-layout-ready={!!legendMeasure}
            data-required-height-mm={legendLayout.requiredHeight}
            style={{
              ...rectStyle(composition.legend.bounds),
              ...compositionTypographyStyle(composition.legend.bodyTypography, preview.pixelsPerMm),
            }}
            tabIndex={0}
          >
            <div
              className="composition-legend-content"
              style={{
                background: composition.legend.background.enabled
                  ? colourWithOpacity(
                      composition.legend.background.colour,
                      composition.legend.background.opacity,
                    )
                  : 'transparent',
                borderColor: composition.legend.border.enabled
                  ? composition.legend.border.colour
                  : 'transparent',
                borderStyle: 'solid',
                borderWidth: `${(composition.legend.border.enabled ? composition.legend.border.widthMm : 0) * preview.pixelsPerMm}px`,
                borderRadius: `${
                  effectiveLegendCornerRadius(
                    composition.legend.cornerRadiusMm,
                    composition.legend.bounds.width * pageMm.width,
                    composition.legend.bounds.height * pageMm.height,
                  ) * preview.pixelsPerMm
                }px`,
                padding: `${composition.legend.paddingTopMm * preview.pixelsPerMm}px ${composition.legend.paddingRightMm * preview.pixelsPerMm}px ${composition.legend.paddingBottomMm * preview.pixelsPerMm}px ${composition.legend.paddingLeftMm * preview.pixelsPerMm}px`,
              }}
            >
              {legendLayout.title ? (
                <h3 style={legendTextStyle(legendLayout.title)}>
                  {legendLines(legendLayout.title)}
                </h3>
              ) : null}
              {legendLayout.columns.map((column, columnIndex) => (
                <div
                  className="composition-legend-column"
                  data-legend-column={columnIndex}
                  key={columnIndex}
                >
                  {column.fragments.map((fragment) => (
                    <section
                      key={fragment.layer.layerId}
                      data-legend-layer={fragment.layer.layerId}
                      data-continued={fragment.continued}
                      data-heading-state={fragment.headingState}
                    >
                      {fragment.heading && fragment.headingState !== 'reserved' ? (
                        <strong style={legendTextStyle(fragment.heading)}>
                          {legendLines(fragment.heading)}
                        </strong>
                      ) : null}
                      {fragment.entries.map((row) => (
                        <div
                          className="composition-legend-entry"
                          key={row.entry.id}
                          data-legend-entry={row.entry.id}
                          data-sample-radius={row.sample.pointRadius}
                          data-sample-line-width={row.sample.lineWidth}
                          style={{
                            position: 'absolute',
                            display: 'block',
                            left: (row.symbol.x - legendBorderMm) * preview.pixelsPerMm,
                            top:
                              (row.label.y -
                                (row.height - row.label.lines.length * row.label.lineHeight) / 2 -
                                legendBorderMm) *
                              preview.pixelsPerMm,
                            width: column.width * preview.pixelsPerMm,
                            height: row.height * preview.pixelsPerMm,
                          }}
                        >
                          <div
                            className="composition-legend-symbol"
                            style={{
                              position: 'absolute',
                              top: ((row.height - row.symbol.height) / 2) * preview.pixelsPerMm,
                              width: row.symbol.width * preview.pixelsPerMm,
                              height: row.symbol.height * preview.pixelsPerMm,
                            }}
                          >
                            {row.entry.annotationSymbol ? (
                              <AnnotationLegendSymbolPreview symbol={row.entry.annotationSymbol} />
                            ) : row.entry.symbols && fragment.layer.geometry !== 'annotation' ? (
                              <SymbolPreview
                                geometry={fragment.layer.geometry}
                                symbols={row.entry.symbols}
                                sample={row.sample}
                              />
                            ) : null}
                          </div>
                          <span
                            style={{
                              ...legendTextStyle(row.label),
                              left: (row.label.x - row.symbol.x) * preview.pixelsPerMm,
                              top:
                                ((row.height - row.label.lines.length * row.label.lineHeight) / 2) *
                                preview.pixelsPerMm,
                            }}
                          >
                            {legendLines(row.label)}
                          </span>
                        </div>
                      ))}
                    </section>
                  ))}
                </div>
              ))}
              {legendLayout.empty ? (
                <span
                  className="composition-legend-empty"
                  style={legendTextStyle(legendLayout.empty)}
                >
                  {legendLines(legendLayout.empty)}
                </span>
              ) : null}
            </div>
            {legendOverflow ? (
              <span role="status" className="composition-legend-overflow">
                {legendFontError
                  ? 'Legend fonts could not load. Reload the page before exporting.'
                  : 'Legend does not fit. Enlarge its box or reduce text size before export.'}
              </span>
            ) : null}
            {selected === 'legend' ? (
              <ResizeHandles
                label="legend"
                onResize={(event, corner) =>
                  beginDrag(
                    event,
                    {
                      kind: 'resize-rect',
                      element: 'legend',
                      startRect: composition.legend.bounds,
                      corner,
                    },
                    'legend',
                  )
                }
              />
            ) : null}
          </div>
        ) : null}

        {active &&
        composition.scaleBar.enabled &&
        hasExactCompositionScale(composition.mapFrame.camera.pitch) &&
        scaleBarMeasurement ? (
          <div
            aria-label="Scale bar"
            className={`composition-scale-bar${selected === 'scale-bar' ? ' composition-element-selected' : ''}`}
            data-ground-distance-metres={scaleBarMeasurement.groundDistanceMetres}
            data-physical-width-mm={scaleBarMeasurement.physicalWidthMm}
            onClick={() => selectElement('scale-bar')}
            onFocus={() => selectElement('scale-bar')}
            onKeyDown={(event) => nudgePoint(event, 'scale-bar', composition.scaleBar.position)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                {
                  kind: 'move-point',
                  element: 'scale-bar',
                  startPosition: composition.scaleBar.position,
                },
                'scale-bar',
              )
            }
            role="button"
            data-label-position={
              composition.scaleBar.style === 'simple-segmented'
                ? (composition.scaleBar.labelPosition ?? 'above')
                : 'below'
            }
            style={{
              ...pointStyle(composition.scaleBar.position),
              ...({
                '--composition-scale-bar-thickness': `${scaleBarStrokeWidthMm(composition.scaleBar.thicknessMm, preview.pixelsPerMm) * preview.pixelsPerMm}px`,
              } as CSSProperties),
              display: 'flex',
              flexDirection:
                composition.scaleBar.style === 'simple-segmented' &&
                composition.scaleBar.labelPosition !== 'below'
                  ? 'column-reverse'
                  : 'column',
              color: composition.scaleBar.colour,
              fontSize: `${composition.scaleBar.fontSizePt * 0.352778 * preview.pixelsPerMm}px`,
              width: `${scaleBarLayout(scaleBarMeasurement.physicalWidthMm, composition.scaleBar.fontSizePt * 0.352778, composition.scaleBar.thicknessMm, composition.scaleBar.panel, 1, composition.scaleBar.style !== undefined && composition.scaleBar.style !== 'line', composition.scaleBar.style === 'simple-segmented' && composition.scaleBar.labelPosition !== 'below').width * preview.pixelsPerMm}px`,
              padding: (composition.scaleBar.panel?.paddingMm ?? 0) * preview.pixelsPerMm,
              border: composition.scaleBar.panel?.border.enabled
                ? `${composition.scaleBar.panel.border.widthMm * preview.pixelsPerMm}px solid ${composition.scaleBar.panel.border.colour}`
                : undefined,
              borderRadius: (composition.scaleBar.panel?.cornerRadiusMm ?? 0) * preview.pixelsPerMm,
              backgroundColor: composition.scaleBar.panel?.background.enabled
                ? colourWithOpacity(
                    composition.scaleBar.panel.background.colour,
                    composition.scaleBar.panel.background.opacity,
                  )
                : undefined,
            }}
            tabIndex={0}
          >
            {composition.scaleBar.style === 'segmented' ||
            composition.scaleBar.style === 'simple-segmented' ? (
              <div
                className="composition-scale-bar-segments"
                style={{
                  height: `${Math.max(composition.scaleBar.fontSizePt * 0.352778 * 0.48, composition.scaleBar.thicknessMm * 2) * preview.pixelsPerMm}px`,
                  borderWidth:
                    scaleBarStrokeWidthMm(composition.scaleBar.thicknessMm, preview.pixelsPerMm) *
                    preview.pixelsPerMm,
                }}
              >
                {(composition.scaleBar.style === 'simple-segmented' ? [0, 1] : [0, 1, 2, 3]).map(
                  (index) => (
                    <span
                      key={index}
                      style={{
                        backgroundColor: index % 2 ? '#ffffff' : composition.scaleBar.colour,
                      }}
                    />
                  ),
                )}
              </div>
            ) : (
              <div
                className="composition-scale-bar-line"
                style={{ borderColor: composition.scaleBar.colour }}
              >
                <span style={{ borderColor: composition.scaleBar.colour }} />
                <span style={{ borderColor: composition.scaleBar.colour }} />
                <span style={{ borderColor: composition.scaleBar.colour }} />
              </div>
            )}
            <div className="composition-scale-bar-labels">
              <span>0</span>
              {composition.scaleBar.style === 'simple-segmented' ? (
                <span className="composition-scale-midpoint">
                  {scaleBarMeasurement.midpointLabel}
                </span>
              ) : null}
              {composition.scaleBar.style === 'simple-segmented' ? (
                <span>{scaleBarMeasurement.label}</span>
              ) : (
                <strong>{scaleBarMeasurement.label}</strong>
              )}
            </div>
          </div>
        ) : null}

        {active && composition.northArrow.enabled ? (
          <div
            aria-label="North arrow"
            className={`composition-north-arrow${selected === 'north-arrow' ? ' composition-element-selected' : ''}`}
            data-bearing={composition.mapFrame.camera.bearing}
            data-rotation={northArrowRotationDegrees(composition.mapFrame.camera.bearing)}
            onClick={() => selectElement('north-arrow')}
            onFocus={() => selectElement('north-arrow')}
            onKeyDown={(event) => nudgePoint(event, 'north-arrow', composition.northArrow.position)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                {
                  kind: 'move-point',
                  element: 'north-arrow',
                  startPosition: composition.northArrow.position,
                },
                'north-arrow',
              )
            }
            role="button"
            style={{
              ...pointStyle(composition.northArrow.position),
              color: composition.northArrow.colour,
              height: `${composition.northArrow.sizeMm * preview.pixelsPerMm}px`,
              width: `${composition.northArrow.sizeMm * preview.pixelsPerMm}px`,
            }}
            tabIndex={0}
          >
            <NorthArrowSymbol
              showLabel={composition.northArrow.showLabel}
              symbolStyle={composition.northArrow.style}
              style={{
                transform: `rotate(${northArrowRotationDegrees(composition.mapFrame.camera.bearing)}deg)`,
              }}
            />
          </div>
        ) : null}

        {active && composition.logo ? (
          <div
            aria-label="Logo"
            className={`composition-logo${selected === 'logo' ? ' composition-element-selected' : ''}`}
            data-logo-mime-type={composition.logo.mimeType}
            onClick={() => selectElement('logo')}
            onFocus={() => selectElement('logo')}
            onKeyDown={(event) => nudgeRect(event, 'logo', composition.logo!.bounds)}
            onPointerDown={(event) =>
              beginDrag(
                event,
                { kind: 'move-rect', element: 'logo', startRect: composition.logo!.bounds },
                'logo',
              )
            }
            role="button"
            style={rectStyle(composition.logo.bounds)}
            tabIndex={0}
          >
            <img alt="" draggable={false} src={composition.logo.dataUrl} />
            {selected === 'logo' ? (
              <ResizeHandles
                label="logo"
                onResize={(event, corner) =>
                  beginDrag(
                    event,
                    {
                      kind: 'resize-rect',
                      element: 'logo',
                      startRect: composition.logo!.bounds,
                      corner,
                    },
                    'logo',
                  )
                }
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
